import React, { useState } from 'react'
import { css } from '@emotion/css'
import { useIntl } from 'react-intl'
import { PureQueryOptions } from 'apollo-client'
import { Library, ShareByEmailType } from '../../../helpers/ui-interfaces'
import { BulkDeleteLibrariesAction } from '../../actions/bulk-delete-action'
import { ExportLibrariesAction } from '../../actions/export-action'
import { SendAction } from '../../actions/send-action'
import { ModifyLibrariesTagsAction } from '../../actions/tag-action'
import { LibraryUsageExportAction } from './library-usage-export-action'
import { ActionButton } from '@contrast-security-inc/design-system-actions'
import { IconSeeMore } from '@contrast-security-inc/design-system-foundations'
import {
  Menu,
  MenuItem,
  MenuTrigger,
  PopoverMenu
} from '@contrast-security-inc/design-system-popovers'
import {
  BulkDeleteLibrariesModal,
  DeleteLibrariesType
} from '../../actions/bulk-delete-modal'
import { Link } from '@contrast-security-inc/design-system-typography'
import { hasRole } from 'contrastBase/RoleSwitch'
import { Role } from '@contrast-security-inc/base-types'
import { useLibraryUsageGridContext } from '../../library-usage-grid/library-usage-context'

export const actionTestID = 'library-summary-see-more-action-icon'

interface Props {
  sourceLink?: string
  refresh: () => Promise<any>
  library: Library
  refetchQueries?: PureQueryOptions[]
  organizationRole: Role
  onClose: () => void
  organizationID: string
  applicationID: string | undefined
  libraryHash: string
}

export function DrilldownActions(props: Props) {
  const {
    organizationID,
    applicationID,
    libraryHash,
    sourceLink,
    refresh,
    library,
    refetchQueries,
    organizationRole,
    onClose
  } = props
  const { hasData } = useLibraryUsageGridContext()
  const { formatMessage } = useIntl()
  const [showDeleteModal, setShowDeleteModal] = useState(false)

  const enabled = hasRole(organizationRole, 'EDIT')

  return (
    <div
      className={css`
        display: flex;
        align-items: center;
      `}
    >
      <span
        className={css`
          svg {
            margin-right: 7px;
          }
        `}
      >
        <ModifyLibrariesTagsAction
          libraries={[library]}
          applicationRole={organizationRole}
          organizationID={organizationID}
          refetchQueries={refetchQueries}
          refresh={refresh}
          isPanel
        />
      </span>

      <span
        className={css`
          svg {
            margin-right: 3px;
          }
        `}
      >
        <SendAction
          showDisabled={enabled}
          role={organizationRole}
          libraries={[library]}
          organizationID={organizationID}
          refetchQueries={refetchQueries}
          refresh={refresh}
          shareType={ShareByEmailType.BY_HASHES}
          renderIcon={true}
        />
      </span>
      <span
        className={css`
          svg {
            margin-right: 3px;
          }
        `}
      >
        <ExportLibrariesAction
          organizationID={organizationID}
          applicationID={applicationID}
          excludedHashes={[]}
          selectedLibraryHashes={[library.hash]}
        />
      </span>

      <PopoverMenu placement="bottom">
        <Menu>
          <>
            {sourceLink && (
              <Link
                className={css`
                  &:hover {
                    text-decoration: none;
                  }
                `}
                href={sourceLink}
                isExternal
                color="neutral100"
              >
                <MenuItem testId="open-in-repository-action">
                  {formatMessage({ id: 'detail_library_seeInRepository' })}
                </MenuItem>
              </Link>
            )}
            <BulkDeleteLibrariesAction
              libraries={[library]}
              showDisabled={enabled}
              organizationRole={organizationRole}
              renderIcon={false}
              setShowDeleteModal={setShowDeleteModal}
            />
            {hasData && applicationID && (
              <LibraryUsageExportAction
                organizationID={organizationID}
                applicationID={applicationID}
                libraryHash={libraryHash}
              />
            )}
          </>
        </Menu>
        <MenuTrigger
          className={css`
            align-items: baseline;
            height: fit-content;
          `}
        >
          {({ isOpen }) => (
            <ActionButton
              data-testid={actionTestID}
              IconButtonProps={{
                'aria-label': formatMessage({ id: 'action_more' }),
                Icon: IconSeeMore
              }}
              TooltipProps={{
                placement: 'top',
                body: formatMessage({ id: 'action_more' }),
                enabled: !isOpen
              }}
            />
          )}
        </MenuTrigger>
      </PopoverMenu>
      {showDeleteModal && (
        <BulkDeleteLibrariesModal
          deleteType={DeleteLibrariesType.BY_HASHES}
          organizationID={organizationID}
          libraries={[library]}
          onSuccess={refresh}
          refetchQueries={refetchQueries}
          showDisabled={enabled}
          organizationRole={organizationRole}
          applicationID={applicationID}
          renderIcon={false}
          onClose={onClose}
          setShowDeleteModal={setShowDeleteModal}
        />
      )}
    </div>
  )
}
